import type { FloorPlanData, CityAsset, PlanAssetData } from "../api/types";
import { appendPlanAssetLights } from "./PlanAssetLights";

/** Shared animation/lighting renderer for plan assets and the plan's background. */
export function animatePlanImage(image: HTMLImageElement, asset: {frames?: string[]; frameDelayMs?: number}): void {
  const frames = (asset.frames ?? []).filter(Boolean);
  if (frames.length < 2 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const delay = Math.max(30, Math.min(5000, asset.frameDelayMs ?? 150));
  let started = 0;
  let previous = -1;
  const tick = (time: number) => {
    if (!image.isConnected) return;
    if (!started) started = time;
    const index = Math.floor((time - started) / delay) % frames.length;
    if (index !== previous && !image.closest("[hidden]")) {
      image.src = frames[index]; previous = index;
    }
    requestAnimationFrame(tick);
  };
  queueMicrotask(() => requestAnimationFrame(tick));
}

export function appendPlanBackground(frame: HTMLElement, plan: FloorPlanData, className: string): void {
  if (!plan.imageUrl || plan.design?.useBackgroundImage === false) return;
  const asset: CityAsset = plan.backgroundAsset ?? {assetId: `background-${plan.id}`, sourceUrl: plan.imageUrl};
  const image = document.createElement("img");
  image.className = className;
  image.src = asset.sourceUrl || plan.imageUrl;
  image.alt = plan.title;
  image.draggable = false;
  image.style.position = "relative";
  image.style.zIndex = String(asset.renderOrder ?? 0);
  image.style.transformOrigin = `${(asset.anchorX ?? 0.5) * 100}% ${(asset.anchorY ?? 0.5) * 100}%`;
  image.style.transform = `translate(${asset.visualOffsetX ?? 0}px,${asset.visualOffsetY ?? 0}px) rotate(${asset.defaultRotation ?? 0}deg) scale(${asset.defaultScale ?? 1})`;
  animatePlanImage(image, asset);
  frame.appendChild(image);
  appendPlanAssetLights(frame, {
    ...asset, id: plan.id, title: plan.title, slug: plan.slug,
    category: "background", imageUrl: image.src, sortOrder: plan.sortOrder, active: true,
  } as PlanAssetData, {
    id: `background-${plan.id}`, assetId: plan.id, x: (asset.anchorX ?? 0.5) * 100, y: (asset.anchorY ?? 0.5) * 100,
    scale: asset.defaultScale ?? 1, rotation: asset.defaultRotation ?? 0, layer: 0, flipX: false,
  }, 1);
}
